import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PromotionsCarousel = () => {
  const [currentPromotion, setCurrentPromotion] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [progress, setProgress] = useState(0);
  const carouselRef = useRef(null);
  const progressIntervalRef = useRef(null);

  const promotions = [
    {
      id: 1,
      title: "Contem Bar",
      subtitle: "Karaokê toda sexta-feira",
      description: "Noite de karaokê toda sexta no Contem Bar. Música, diversão e ambiente descontraído.",
      image: "https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=600&h=400&fit=crop",
      discount: "Karaokê",
      validUntil: "Sexta-feira",
      stores: "Contem Bar",
      category: "Entretenimento",
      color: "from-purple-600 to-pink-600",
      iconName: "Mic",
      notice: "Confirmação: entrar em contato com o estabelecimento promotor."
    },
    {
      id: 2,
      title: "Contem Bar",
      subtitle: "Encontro de carros antigos",
      description: "Encontro de carros antigos toda segunda quinta-feira do mês no Contem Bar.",
      image: "https://images.unsplash.com/photo-tbGij0JZojE?w=600&h=400&fit=crop",
      discount: "Encontro",
      validUntil: "2ª quinta do mês",
      stores: "Contem Bar",
      category: "Eventos",
      color: "from-amber-500 to-red-500",
      iconName: "Car",
      notice: "Confirmação: entrar em contato com o estabelecimento promotor."
    },
    {
      id: 3,
      title: "Tao do Grão",
      subtitle: "Pizza toda sexta-feira",
      description: "Sexta à noite com pizza no Tao do Grão. Ideal para reunir a família e amigos.",
      image: "/images/lojistas/tao.png",
      discount: "Pizza",
      validUntil: "Sexta à noite",
      stores: "Tao do Grão",
      category: "Gastronomia",
      color: "from-orange-500 to-yellow-500",
      iconName: "Pizza",
      notice: "Confirmação: entrar em contato com o estabelecimento promotor."
    }
  ];

  const AUTO_PLAY_INTERVAL = 6000;

  // Auto-play desabilitado quando há apenas uma promoção
  useEffect(() => {
    if (promotions?.length <= 1 || isPaused) return;

    const timer = setInterval(() => {
      setCurrentPromotion((prev) => (prev + 1) % promotions?.length);
      setProgress(0);
    }, AUTO_PLAY_INTERVAL);

    return () => clearInterval(timer);
  }, [isPaused, promotions?.length]);

  // Progress bar animation
  useEffect(() => {
    if (isPaused) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      return;
    }

    setProgress(0);
    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          return 0;
        }
        return prev + (100 / (AUTO_PLAY_INTERVAL / 100));
      });
    }, 100);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [currentPromotion, isPaused]);

  const goToPromotion = (index) => {
    setCurrentPromotion(index);
    setProgress(0);
  };

  const nextPromotion = () => {
    setCurrentPromotion((prev) => (prev + 1) % promotions?.length);
    setProgress(0);
  };

  const prevPromotion = () => {
    setCurrentPromotion((prev) => (prev - 1 + promotions?.length) % promotions?.length);
    setProgress(0);
  };

  // Touch handlers para swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextPromotion();
    }
    if (isRightSwipe) {
      prevPromotion();
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Promoções Exclusivas
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Não perca as melhores ofertas e eventos especiais da nossa galeria comercial
          </p>
        </div>

        {/* Carousel Container */}
        <div 
          ref={carouselRef}
          className="relative overflow-hidden rounded-2xl shadow-warm-lg group"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="relative h-[500px] lg:h-[600px]">
            {promotions?.map((promotion, index) => (
              <div
                key={promotion?.id}
                className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                  index === currentPromotion 
                    ? 'opacity-100 translate-x-0 z-10' 
                    : index < currentPromotion 
                      ? 'opacity-0 -translate-x-full z-0' 
                      : 'opacity-0 translate-x-full z-0'
                }`}
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <Image
                    src={promotion?.image}
                    alt={promotion?.title}
                    className="w-full h-full object-cover scale-105 transition-transform duration-700 group-hover:scale-100"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-r ${promotion?.color} opacity-85`}></div>
                </div>

                {/* Content */}
                <div className="relative z-10 h-full flex items-center py-12 lg:py-16">
                  <div className="w-full max-w-4xl mx-auto px-8 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                      {/* Text Content */}
                      <div className={`text-white transform transition-all duration-700 delay-150 ${
                        index === currentPromotion 
                          ? 'opacity-100 translate-y-0' 
                          : 'opacity-0 translate-y-4'
                      }`}>
                        <div className="flex items-center flex-wrap gap-3 mb-4">
                          <span className="bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-sm font-medium border border-white/30 shadow-lg">
                            {promotion?.category}
                          </span>
                          <span className="bg-accent text-accent-foreground px-4 py-1.5 rounded-full text-sm font-bold shadow-lg animate-pulse-gentle">
                            {promotion?.discount}
                          </span>
                        </div>
                        
                        <h3 className="text-3xl lg:text-5xl font-heading font-bold mb-3 leading-tight drop-shadow-lg">
                          {promotion?.title}
                        </h3>
                        
                        <p className="text-xl lg:text-2xl font-semibold mb-4 text-white/95 drop-shadow-md">
                          {promotion?.subtitle}
                        </p>
                        
                        <p className="text-base lg:text-lg mb-4 text-white/85 leading-relaxed drop-shadow-sm">
                          {promotion?.description}
                        </p>
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-sm text-white/95 border border-white/30 shadow-md mb-6">
                          <Icon name="Info" size={16} className="text-white/90" />
                          <span className="font-medium">{promotion?.notice}</span>
                        </div>

                        {/* Promotion Details */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="bg-white/15 backdrop-blur-md rounded-xl p-4 border border-white/20 shadow-lg hover:bg-white/20 transition-colors">
                            <div className="flex items-center space-x-2 mb-1">
                              <Icon name="Calendar" size={18} className="text-white/90" />
                              <span className="text-sm font-medium text-white/90">Válido até</span>
                            </div>
                            <div className="font-bold text-white text-lg">{promotion?.validUntil}</div>
                          </div>
                          <div className="bg-white/15 backdrop-blur-md rounded-xl p-4 border border-white/20 shadow-lg hover:bg-white/20 transition-colors">
                            <div className="flex items-center space-x-2 mb-1">
                              <Icon name="Store" size={18} className="text-white/90" />
                              <span className="text-sm font-medium text-white/90">Participantes</span>
                            </div>
                            <div className="font-bold text-white text-lg">{promotion?.stores}</div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-4">
                          <Link to="/store-directory">
                            <Button 
                              variant="default"
                              size="lg"
                              className="bg-white text-primary hover:bg-white/95 hover:scale-105 font-semibold px-8 shadow-xl transition-all duration-300"
                            >
                              Ver Lojas Participantes
                              <Icon name="ArrowRight" size={20} className="ml-2" />
                            </Button>
                          </Link>
                        </div>
                      </div>

                      {/* Visual Element */}
                      <div className={`hidden lg:flex items-center justify-center transform transition-all duration-700 delay-300 ${
                        index === currentPromotion 
                          ? 'opacity-100 scale-100' 
                          : 'opacity-0 scale-95'
                      }`}>
                        <div className="bg-white/15 backdrop-blur-md rounded-2xl p-10 text-center border border-white/20 shadow-2xl">
                          <Icon name={promotion?.iconName || "Sparkles"} size={80} className="text-white mb-4 mx-auto drop-shadow-lg" />
                          <div className="text-3xl text-white/95 font-bold drop-shadow-md mb-2">
                            {promotion?.discount}
                          </div>
                          <div className="mt-4 text-white/85 font-medium">
                            {promotion?.validUntil}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Progress Bar - Oculto quando há apenas uma promoção */}
          {promotions?.length > 1 && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-white/10 z-30">
              <div 
                className="h-full bg-white transition-all duration-100 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* Navigation Controls - Oculto quando há apenas uma promoção */}
          {promotions?.length > 1 && (
            <div className="absolute bottom-8 lg:bottom-10 left-1/2 transform -translate-x-1/2 z-30">
              <div className="flex items-center space-x-3 bg-black/30 backdrop-blur-md rounded-full px-4 py-2 border border-white/20 shadow-xl">
                <button
                  onClick={prevPromotion}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 active:scale-95"
                  aria-label="Promoção anterior"
                >
                  <Icon name="ChevronLeft" size={18} />
                </button>
                
                <div className="flex space-x-2 mx-2">
                  {promotions?.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToPromotion(index)}
                      className={`transition-all duration-300 rounded-full ${
                        index === currentPromotion 
                          ? 'bg-white w-8 h-2.5 scale-110' 
                          : 'bg-white/50 hover:bg-white/70 w-2.5 h-2.5'
                      }`}
                      aria-label={`Ir para promoção ${index + 1}`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextPromotion}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 active:scale-95"
                  aria-label="Próxima promoção"
                >
                  <Icon name="ChevronRight" size={18} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            Acompanhe todas as novidades e promoções
          </p>
          <Link to="/store-directory">
            <Button variant="outline" size="lg" className="font-semibold hover:scale-105 transition-transform duration-200">
              Ver Todas as Lojas
              <Icon name="Store" size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PromotionsCarousel;